import classNames from 'classnames';
import * as React from 'react';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { List } from '~/Partials/List/List';
import { omitProperty } from '~/Partials/Utils/utils';
import { useWindowWidthDetection } from '~/Utils/Hooks/useWindowWidthDetection';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import type { ArticleCardProps } from './ArticleCard/ArticleCard';
import { ArticleCard } from './ArticleCard/ArticleCard';
import styles from './ArticleList.module.css';
import type { ArticleListVariant } from './ArticleListVariant';
import { MAX_ITEMS_TO_DISPLAY, resolveCardDisplayProps, resolveListLayoutClass } from './Utils/ArticleListUtils';

export interface ArticleListItemProps extends Pick<ArticleCardProps, 'title' | 'articleUrl' | 'image' | 'description'> {
    id: string;
}

export interface ArticleListProps {
    id: string;
    items: ArticleListItemProps[];
    heading?: HtmlString;
    teaser?: HtmlString;
    variant?: ArticleListVariant;
}

export const ArticleList: React.FC<ArticleListProps> = props => {
    const { id, heading, items, teaser, variant = 'default' } = props;

    const isMobile = useWindowWidthDetection({ condition: 'lessThan', breakpoint: MediaBreakpoints.Tablet });
    const filteredItems = items.slice(0, MAX_ITEMS_TO_DISPLAY);

    const richtextVariant = variant === 'dark' ? 'light' : 'dark';

    return (
        <PageSection
            id={id}
            className={classNames(styles.ArticleList, variant !== 'default' && styles[`Variant--${variant}`])}
        >
            <div className={styles.Header}>
                {heading && <PageSectionHeading heading={heading} className={styles.Heading} />}
                {teaser && (
                    <HtmlDiv
                        html={teaser}
                        className={classNames(styles.Teaser, 'RichtextContent', `RichtextContent--${richtextVariant}`)}
                    />
                )}
            </div>
            <List
                className={classNames(styles.List, resolveListLayoutClass(filteredItems.length))}
                items={filteredItems}
                renderItem={(item, index) => (
                    <li key={item.id} className={styles.ListItem}>
                        <ArticleCard
                            {...(index === 0 ? item : omitProperty(item, 'description'))}
                            {...resolveCardDisplayProps(index, filteredItems.length, isMobile !== false)}
                            typography={index === 0 ? 'large' : 'small'}
                        />
                    </li>
                )}
            />
        </PageSection>
    );
};
